import React, { useMemo, useState } from 'react';
import {
  COUNTIES_BY_STATE,
  COUNTY_BY_FIPS,
  countyLabel,
  decideCounty,
  formatServes,
  stateName,
  statesForPicker,
} from '../shared/serviceArea';

const STATE_CODES = statesForPicker();

function emptyGroup(state) {
  return { state: state || 'FL', statewide: false, fips: [] };
}

function groupsFromArea(area) {
  const fips = (area && area.service_county_fips) || [];
  const statewide = new Set((area && area.service_statewide) || []);
  const order = [];
  const grouped = {};
  fips.forEach((code) => {
    const county = COUNTY_BY_FIPS[code];
    if (!county) return;
    if (!grouped[county.state]) {
      grouped[county.state] = emptyGroup(county.state);
      order.push(county.state);
    }
    grouped[county.state].fips.push(code);
  });
  statewide.forEach((code) => {
    if (!grouped[code]) {
      grouped[code] = emptyGroup(code);
      order.push(code);
    }
    grouped[code].statewide = true;
    grouped[code].fips = [];
  });
  if (order.length === 0) return [emptyGroup('FL')];
  return order.map((code) => grouped[code]);
}

function areaFromGroups(groups) {
  const countyFips = [];
  const statewide = [];
  groups.forEach((group) => {
    if (!group.state) return;
    if (group.statewide) {
      if (!statewide.includes(group.state)) statewide.push(group.state);
      return;
    }
    group.fips.forEach((code) => {
      if (!countyFips.includes(code)) countyFips.push(code);
    });
  });
  return { service_county_fips: countyFips, service_statewide: statewide };
}

export function ServiceAreaFields({ value, onChange, colors, error }) {
  const [groups, setGroups] = useState(() => groupsFromArea(value));
  const [queries, setQueries] = useState({});
  const text = colors && colors.text;
  const muted = colors && colors.textSecondary;
  const border = (colors && colors.border) || '#d1d5db';
  const surface = (colors && colors.surfaceAlt) || '#fff';

  const emit = (next) => {
    setGroups(next);
    if (onChange) onChange(areaFromGroups(next));
  };

  const update = (index, patch) => {
    emit(groups.map((group, groupIndex) => (groupIndex === index ? { ...group, ...patch } : group)));
  };

  return (
    <div data-testid="service-area-fields">
      {groups.map((group, index) => {
        const counties = COUNTIES_BY_STATE[group.state] || [];
        const query = (queries[index] || '').trim().toLowerCase();
        const visible = counties.filter((county) => !query || county.name.toLowerCase().includes(query));
        const selected = new Set(group.fips);
        const stateLabel = group.state ? stateName(group.state) : '';
        const stateChoices = STATE_CODES.filter((code) => (
          code === group.state || !groups.some((other, otherIndex) => otherIndex !== index && other.state === code)
        ));
        return (
          <div key={`${group.state || 'new'}-${index}`} style={{ marginTop: index === 0 ? 0 : '1rem' }}>
            <label style={{ display: 'block', fontWeight: 650, marginBottom: '0.35rem', color: text }}>
              State served *
            </label>
            <select
              aria-label={index === 0 ? 'State served' : `State served ${index + 1}`}
              value={group.state}
              onChange={(event) => update(index, { state: event.target.value, statewide: false, fips: [] })}
              style={fieldStyle(surface, border, text)}
            >
              {group.state ? null : <option value="">Choose a state</option>}
              {stateChoices.map((code) => (
                <option key={code} value={code}>{stateName(code)}</option>
              ))}
            </select>
            {group.state ? (
            <>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', margin: '0.75rem 0 0.35rem' }}>
              <span style={{ fontWeight: 650, color: text }}>
                Counties served * {group.statewide ? '(entire state)' : `(${group.fips.length} selected)`}
              </span>
              {groups.length > 1 ? (
                <button type="button" onClick={() => emit(groups.filter((_group, groupIndex) => groupIndex !== index))} style={linkButton}>
                  Remove state
                </button>
              ) : null}
            </div>
            {group.fips.length > 0 && !group.statewide ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '0.5rem' }}>
                {group.fips.map((code) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => update(index, { fips: group.fips.filter((item) => item !== code) })}
                    style={chipStyle}
                  >
                    {(COUNTY_BY_FIPS[code] && COUNTY_BY_FIPS[code].name.replace(/ County$/, '')) || code} ×
                  </button>
                ))}
              </div>
            ) : null}
            <input
              type="search"
              aria-label={`Search ${stateLabel} counties`}
              placeholder={`Search ${stateLabel} counties...`}
              value={queries[index] || ''}
              onChange={(event) => setQueries((current) => ({ ...current, [index]: event.target.value }))}
              style={fieldStyle(surface, border, text)}
            />
            <div style={{ maxHeight: '220px', overflowY: 'auto', border: `1px solid ${border}`, borderRadius: '0.5rem', marginTop: '0.4rem' }}>
              <label style={rowStyle(text)}>
                <input
                  type="checkbox"
                  checked={group.statewide}
                  onChange={(event) => update(index, { statewide: event.target.checked, fips: [] })}
                />
                Entire state of {stateLabel}
              </label>
              {visible.map((county) => (
                <label key={county.fips} style={{ ...rowStyle(text), opacity: group.statewide ? 0.45 : 1 }}>
                  <input
                    type="checkbox"
                    disabled={group.statewide}
                    checked={!group.statewide && selected.has(county.fips)}
                    onChange={(event) => {
                      const next = event.target.checked
                        ? group.fips.concat(county.fips)
                        : group.fips.filter((code) => code !== county.fips);
                      update(index, { statewide: false, fips: next });
                    }}
                  />
                  {county.name.replace(/ County$/, '')}
                </label>
              ))}
            </div>
            </>
            ) : groups.length > 1 ? (
              <button type="button" onClick={() => emit(groups.filter((_group, groupIndex) => groupIndex !== index))} style={{ ...linkButton, marginTop: '0.35rem' }}>
                Remove state
              </button>
            ) : null}
          </div>
        );
      })}
      <button
        type="button"
        onClick={() => {
          emit(groups.concat({ state: '', statewide: false, fips: [] }));
        }}
        style={{ ...linkButton, marginTop: '0.6rem' }}
      >
        + Add another state
      </button>
      {error ? (
        <p role="alert" style={{ color: '#b45309', margin: '0.45rem 0 0' }}>{error}</p>
      ) : (
        <p style={{ color: muted, margin: '0.45rem 0 0', fontSize: '0.9rem' }}>
          Pick at least one county, or entire state.
        </p>
      )}
    </div>
  );
}

export function ServesLine({ record, match, missingPrompt, onAdd }) {
  const text = formatServes(record);
  if (text) {
    return (
      <p style={{ margin: '0 0 0.75rem' }}>
        <span style={{ fontWeight: 650 }}>{text}</span>
        {match ? (
          <span style={{ display: 'block', marginTop: '0.2rem', fontWeight: 650, color: '#0f766e' }}>
            Serves your area
          </span>
        ) : null}
      </p>
    );
  }
  if (!missingPrompt || !onAdd) return null;
  return (
    <button
      type="button"
      onClick={onAdd}
      style={{
        margin: '0 0 0.75rem',
        background: '#fff7ed',
        color: '#9a3412',
        border: '1px solid #fdba74',
        borderRadius: '999px',
        minHeight: '36px',
        padding: '0.25rem 0.75rem',
        fontWeight: 700,
        cursor: 'pointer',
      }}
    >
      Add service area
    </button>
  );
}

export function CountyField({
  zip,
  state,
  fips,
  source,
  onChange,
  colors,
  loadCandidates,
}) {
  const [candidates, setCandidates] = useState([]);
  const [choosing, setChoosing] = useState(false);
  const [loadedZip, setLoadedZip] = useState('');
  const previousZip = React.useRef(null);
  const previousState = React.useRef(null);
  const text = colors && colors.text;
  const border = (colors && colors.border) || '#d1d5db';
  const surface = (colors && colors.surface) || '#fff';
  const normalized = String(zip || '').replace(/\D/g, '').slice(0, 5);
  const st = String(state || '').trim().toUpperCase();

  React.useEffect(() => {
    let cancelled = false;
    if (normalized.length < 5) {
      setCandidates([]);
      setLoadedZip('');
      return undefined;
    }
    const load = loadCandidates || defaultCandidates;
    load(normalized, st).then((rows) => {
      if (cancelled) return;
      setCandidates(rows || []);
      setLoadedZip(normalized);
    }).catch(() => {
      if (!cancelled) {
        setCandidates([]);
        setLoadedZip(normalized);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [normalized, st, loadCandidates]);

  const decision = useMemo(() => decideCounty({
    zip: normalized,
    state: st,
    candidates: loadedZip === normalized ? candidates : [],
    pick: fips,
    source,
    zipChanged: false,
    previousSource: source,
    previousFips: fips,
  }), [normalized, st, candidates, loadedZip, fips, source]);

  const apply = (nextFips, nextSource) => {
    if (onChange) {
      onChange({
        property_county_fips: nextFips || '',
        property_county_source: nextSource || '',
        county_pending: false,
      });
    }
  };

  React.useEffect(() => {
    if (loadedZip !== normalized || normalized.length < 5) return;
    const seenBefore = previousZip.current !== null;
    const zipChanged = seenBefore && (previousZip.current !== normalized || previousState.current !== st);
    previousZip.current = normalized;
    previousState.current = st;
    const next = decideCounty({
      zip: normalized,
      state: st,
      candidates,
      pick: zipChanged ? '' : fips,
      source: zipChanged ? '' : source,
      zipChanged,
      previousSource: zipChanged ? '' : source,
      previousFips: zipChanged ? '' : fips,
    });
    if (next.mode === 'auto' && (fips !== next.fips || source !== 'zip')) {
      apply(next.fips, 'zip');
      setChoosing(false);
    }
    if (next.mode === 'split' && zipChanged && fips) apply('', '');
    // Fill an unambiguous ZIP once. A later county pick stays until the ZIP changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedZip, normalized, st, candidates]);

  const stateCounties = COUNTIES_BY_STATE[/^[A-Z]{2}$/.test(st) ? st : 'FL'] || [];
  const label = countyLabel(fips);
  const waiting = normalized.length >= 5 && loadedZip !== normalized;
  const showSplit = !waiting && decision.mode === 'split' && candidates.length > 1;
  const showAuto = !waiting && decision.mode === 'auto' && label && !choosing;
  const pendingSplit = showSplit && !String(fips || '');
  const reportedPending = React.useRef(null);

  React.useEffect(() => {
    if (reportedPending.current === pendingSplit) return;
    reportedPending.current = pendingSplit;
    if (onChange) onChange({ county_pending: pendingSplit });
    // Tell the form a split ZIP still needs a county. A pick clears it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSplit]);

  return (
    <div data-testid="property-county" style={{ marginTop: '0.75rem' }}>
      <label style={{ display: 'block', fontWeight: 650, marginBottom: '0.35rem', color: text }}>
        County{showSplit ? ' *' : ''}
      </label>
      {normalized.length < 5 && !fips ? (
        <p style={{ margin: '0 0 0.4rem', color: colors && colors.textSecondary }}>Add ZIP to set county</p>
      ) : null}
      {waiting ? (
        <p style={{ margin: '0 0 0.4rem', color: colors && colors.textSecondary }}>Checking ZIP...</p>
      ) : null}
      {showAuto ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
          <span style={{ color: text }}>{label.replace(`, ${st || 'FL'}`, '')}</span>
          <span style={{ color: colors && colors.textSecondary, fontSize: '0.9rem' }}>Auto-filled from ZIP</span>
          <button type="button" onClick={() => setChoosing(true)} style={linkButton}>Change</button>
        </div>
      ) : null}
      {showSplit ? (
        <fieldset style={{ border: `1px solid ${border}`, borderRadius: '0.5rem', margin: 0, padding: '0.75rem' }}>
          <legend style={{ padding: '0 0.25rem', color: text }}>
            ZIP {normalized} is in {candidates.length} counties. Which one is this home in?
          </legend>
          {candidates.map((row) => (
            <label key={row.fips} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', minHeight: '44px', color: text }}>
              <input
                type="radio"
                name={`county-${normalized}`}
                checked={String(fips) === String(row.fips)}
                onChange={() => apply(String(row.fips), 'picked')}
              />
              {row.name}
            </label>
          ))}
          <p style={{ margin: '0.35rem 0 0', color: colors && colors.textSecondary, fontSize: '0.9rem' }}>
            Not sure? Check the property tax bill or the county property appraiser site.
          </p>
        </fieldset>
      ) : null}
      {!waiting && !showAuto && !showSplit ? (
        <select
          aria-label="County"
          value={fips || ''}
          onChange={(event) => apply(event.target.value, event.target.value ? 'picked' : '')}
          style={fieldStyle(surface, border, text)}
        >
          <option value="">Choose a county</option>
          {stateCounties.map((county) => (
            <option key={county.fips} value={county.fips}>{county.name}</option>
          ))}
        </select>
      ) : null}
    </div>
  );
}

async function defaultCandidates(zip, state) {
  const { supabase } = require('../supabaseClient');
  const { data, error } = await supabase.rpc('zip_county_candidates', {
    p_zip: zip,
    p_state: state || null,
  });
  if (error) throw error;
  return data || [];
}

function fieldStyle(surface, border, text) {
  return {
    width: '100%',
    boxSizing: 'border-box',
    minHeight: '44px',
    padding: '0.55rem 0.7rem',
    borderRadius: '0.5rem',
    border: `1px solid ${border}`,
    background: surface,
    color: text,
    fontSize: '16px',
  };
}

const rowStyle = (text) => ({
  display: 'flex',
  gap: '0.55rem',
  alignItems: 'center',
  minHeight: '40px',
  padding: '0.15rem 0.7rem',
  color: text,
});

const chipStyle = {
  border: '1px solid #99f6e4',
  background: '#ccfbf1',
  color: '#115e59',
  borderRadius: '999px',
  minHeight: '32px',
  padding: '0.15rem 0.65rem',
  cursor: 'pointer',
};

const linkButton = {
  background: 'transparent',
  border: 'none',
  color: '#0f766e',
  fontWeight: 700,
  cursor: 'pointer',
  padding: 0,
  minHeight: '44px',
};
